import React, { useMemo } from 'react';

import { isEmpty, isNil, keyBy } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { linkedRequirementIds } from '@modules/advancement/constants';
import { userSubUnitMembersSel } from '@modules/advancement/packRoster';
import type {
  GroupedPendingItem,
  GroupedPendingItemChild,
  PendingItem,
  RecordedItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import {
  determineAuditData,
  isYouthInUserSubunitChecker,
} from '@modules/advancement/pendingItems/utils';
import CommentsButton from '@modules/advancement/unitAdvancement/components/CommentsButton/CommentsButton';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  advancementStatusDate,
  goToYouthAdvRequirements,
} from '@modules/shared';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import DateView from '@shared/components/DateView';
import PersonAvatar from '@shared/components/PersonAvatar';
import S from '@shared/components/S';
import { advancementTypes } from '@shared/constants';
import {
  adventuresSel,
  allAwardsSel,
  meritBadgesSel,
  ranksSel,
  shouldFilterUsersBySubunitsSel,
} from '@shared/duck/selectors/general.selectors';

import styles from './PendingItemsTable.less';

function isRowGroup(row: { children: Array<unknown> }) {
  return row?.children && !isEmpty(row?.children);
}

export function RequirementAdvancementType(props: {
  row: PendingItem & { isUserInSubUnit?: boolean };
}) {
  const { row } = props;
  const dispatch = useDispatch();

  const mbDict = useSelector(meritBadgesSel);
  const awardsDict = useSelector(allAwardsSel);
  const ranksDict = useSelector(ranksSel);
  const adventuresDict = useSelector(adventuresSel);
  const mbsById = useMemo(() => keyBy(mbDict, 'id'), [mbDict]);
  const ranksById = useMemo(() => keyBy(ranksDict, 'id'), [ranksDict]);
  const awardsById = useMemo(() => keyBy(awardsDict, 'id'), [awardsDict]);
  const adventuresById = useMemo(
    () => keyBy(adventuresDict, 'id'),
    [adventuresDict],
  );

  if (!row) return null;

  let imgSrc = '';
  let link = '';
  const userId = row.userId;
  const advancementId = row.advancementId;
  const advancementName = row?.advancementName || '';
  let onClick: () => void;
  switch (row.advancementType) {
    case advancementTypes.MERIT_BADGES: {
      imgSrc = mbsById[row.advancementId]?.imageUrl100;
      const mbId = row.advancementId;
      link = `/youthProfile/${userId}/meritBadges/${mbId}`;
      onClick = () => {
        dispatch(
          goToYouthAdvRequirements({
            userId,
            advancementType: advancementTypes.MERIT_BADGES,
            advancementId,
          }),
        );
      };
      break;
    }
    case advancementTypes.RANKS: {
      imgSrc = ranksById[row.advancementId]?.imageUrl100;
      const awardId = row.advancementId;
      onClick = () => {
        dispatch(
          goToYouthAdvRequirements({
            userId,
            advancementType: advancementTypes.RANKS,
            advancementId: awardId,
          }),
        );
      };
      break;
    }
    case advancementTypes.ADVENTURES: {
      imgSrc = adventuresById[advancementId]?.imageUrl100;
      onClick = () => {
        dispatch(
          goToYouthAdvRequirements({
            userId,
            advancementType: advancementTypes.ADVENTURES,
            advancementId,
          }),
        );
      };
      break;
    }
    case advancementTypes.AWARDS: {
      imgSrc = awardsById[row.advancementId]?.imageUrl100;
      const awardId = row.advancementId;
      onClick = () => {
        dispatch(
          goToYouthAdvRequirements({
            userId,
            advancementType: advancementTypes.AWARDS,
            advancementId: awardId,
          }),
        );
      };
      break;
    }
  }
  return (
    <div
      className={styles.advancementTypeName}
      data-advancementid={row?.advancementId}
      data-requirementid={row?.id}
    >
      {imgSrc ? (
        <img src={imgSrc} alt={advancementName} />
      ) : (
        <div className={styles.noImagePlaceholder} />
      )}
      {!isNil(row.isUserInSubUnit) && row.isUserInSubUnit ? (
        <a
          onClick={e => {
            e.preventDefault();
            onClick();
          }}
          rel="noreferrer"
          target="_blank"
          href={link}
          title={advancementName}
        >
          {advancementName}
        </a>
      ) : (
        <S size="4" bold inlineBlock>
          {advancementName}
        </S>
      )}
    </div>
  );
}

export function NameContainerChecker(memberId: number) {
  const userSubUnitMembers = useSelector(userSubUnitMembersSel);
  const shouldFilterUsersBySubunits = useSelector(
    shouldFilterUsersBySubunitsSel,
  );

  const userSubUnitUsersMIDs: number[] = useMemo(
    () =>
      userSubUnitMembers
        .map(member => member.memberId)
        .filter(memberId => !isNil(memberId)) as number[],
    [userSubUnitMembers],
  );

  return isYouthInUserSubunitChecker(
    shouldFilterUsersBySubunits,
    userSubUnitUsersMIDs,
    memberId,
  );
}

export const groupedColumns = ({
  onGoToYouthProfile,
  onGoToAdultProfile,
}: {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
}) => [
  {
    key: 'name',
    width: '220px',
    title: <FormattedMessage id="advancement.PendingItems.column.name" />,
    dataIndex: 'name',
    render(_value: string, row: GroupedPendingItem) {
      const isGrouped = isRowGroup(row);

      const target = isGrouped ? row?.children[0] : row;

      const {
        userId,
        isAdult,
        profilePictureUrl,
        activityTypeId,
        firstName,
        lastName,
        nickName,
        isUserInSubUnit,
      } = target as GroupedPendingItemChild & { isUserInSubUnit?: boolean };

      const personPreferredName = getPreferredName({
        firstName,
        lastName,
        nickName: nickName as string,
      });

      let onClickName = undefined;
      if (isAdult) {
        onClickName = onGoToAdultProfile(userId, activityTypeId);
      } else {
        onClickName = onGoToYouthProfile(userId, activityTypeId);
      }

      if (!isNil(isUserInSubUnit) && isUserInSubUnit) {
        return (
          <div className={styles.nameContainer}>
            <PersonAvatar isAdult={isAdult} src={profilePictureUrl} />
            <S
              size="4"
              bold
              inline
              colored
              className={styles.youthName}
              onClick={onClickName}
            >
              {personPreferredName}
            </S>
          </div>
        );
      }
      return (
        <div className={styles.nameContainerNotActive}>
          <PersonAvatar isAdult={isAdult} src={profilePictureUrl} />
          <S size="4" bold inlineBlock>
            {personPreferredName}
          </S>
        </div>
      );
    },
  },
  {
    key: 'advancementType',
    dataIndex: 'advancementType',
    width: '160px',
    render(_type: string, row: GroupedPendingItem | PendingItem) {
      if (isRowGroup(row as GroupedPendingItem)) return null;

      return (
        <RequirementAdvancementType
          row={row as PendingItem & { isUserInSubUnit: boolean }}
        />
      );
    },
  },
  {
    key: 'commentButton',
    dataIndex: 'commentButton',
    width: '80px',
    onCell: () => ({
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
      },
    }),
    render(_type: string, row: GroupedPendingItem | PendingItem) {
      if (isRowGroup(row as GroupedPendingItem)) return null;

      const pendingItem = row as PendingItem;

      return (
        <CommentsButton
          advancementId={pendingItem.advancementId}
          advancementType={pendingItem.advancementType}
          renderPhotos
          requirementId={pendingItem.id}
          requirementName={pendingItem.requirementName}
          userId={pendingItem.userId}
          versionId={pendingItem.versionId}
        />
      );
    },
  },
  {
    title: <FormattedMessage id="advancement.PendingItems.column.memberId" />,
    key: 'memberId',
    dataIndex: 'memberId',
    width: '15ch',
  },
  {
    key: 'description',
    dataIndex: 'description',
    width: '240px',
    render(val: string, row: RecordedItem) {
      const div = document.createElement('p');
      div.innerHTML = val;
      const clean = div.textContent || div.innerText || '';

      const MAX_CHARS = 30;
      const display =
        clean.length < MAX_CHARS
          ? clean
          : clean.substring(0, MAX_CHARS).trimEnd() + '...';

      const auditData = determineAuditData(row);

      return (
        <div>
          {display}
          {!isEmpty(auditData) && (
            <S size="6" className={styles.audit} italic>
              <FormattedMessage
                id="advancement.PendingItems.audit"
                values={{
                  status: auditData.status,
                  name: auditData.name ?? '',
                  date: auditData.date,
                }}
              />
            </S>
          )}
        </div>
      );
    },
    title: (
      <FormattedMessage id="advancement.PendingItems.column.itemDescription" />
    ),
  },
  {
    key: 'note',
    dataIndex: 'note',
    render(value: string, record: PendingItem) {
      const parentRequirementId = (record as PendingItem).parentRequirementId;
      if (
        parentRequirementId === undefined ||
        !linkedRequirementIds.includes(parentRequirementId) ||
        !featureFlags.getFlag('SBL_6123_SHOW_LINKED_REQ_NOTE')
      )
        return null;

      return (
        <div>
          <Text color="color-red" font="body-caption">
            <FormattedMessage id="advancement.PendingItems.column.linkedRequirementNote" />
          </Text>
        </div>
      );
    },
    onCell: () => ({
      style: { minWidth: '200px' },
    }),
    onHeaderCell: () => ({
      style: { minWidth: '200px' },
    }),
  },
  {
    key: 'completionDate',
    dataIndex: 'date',
    width: '10em',
    render(value: string, record: PendingItem) {
      return (
        <DateView value={value || advancementStatusDate.getCompleted(record)} />
      );
    },
    title: <FormattedMessage id="advancement.PendingItems.column.itemDate" />,
  },
];
